import { AdditionalModule, ImplementationPackage, SubscriptionData, SubscriptionLine, SubscriptionPackage } from "../BillingTypes";


// Convert a price value to a number, falls back to zero for missing values
export const safePriceNumber = (value: string | number | null | undefined) => {
    const numberValue = Number(value);
    return isNaN(numberValue) ? 0 : numberValue;
};


// Format a price amount based on the user locale, without decimals for round amounts
export const formatPriceOnUserLocale = (amount: number, userLocale: string) => {

    // Only show two decimals when the amount is not a round number
    const decimalPlaces = amount % 1 !== 0 ? 2 : 0;

    // Format the amount based on the user locale
    return new Intl.NumberFormat(userLocale, {
        style: "decimal",
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(amount);
};


// Determine whether the environment type can order paid features, admins
// keep the order buttons so superusers can test and fix environments
export const canOrderPaidFeatures = (environmentType?: string | null) => {
    return !['trial', 'beta'].includes(environmentType ?? '');
};


// Get the monthly price of a module for the given total seats
export const getModuleMonthlyPriceForSeats = (moduleOption: AdditionalModule, totalSeats: number) => {

    // Return the flat price for per unit priced modules
    if (moduleOption.price_type === 'per_unit') {
        return safePriceNumber(moduleOption.flat_price);
    }

    // Return the maximized price when the seats exceed the threshold
    if (moduleOption.enable_threshold && totalSeats > moduleOption.threshold) {
        return safePriceNumber(moduleOption.price_above_threshold);
    }

    // Return the price per seat multiplied by the total seats
    return safePriceNumber(moduleOption.price_per_seat) * totalSeats;
};


// Get the total entered seats of a subscription
export const getTotalEnteredSeats = (subscription: SubscriptionData) => {
    return parseInt(subscription.seats.normal || '0')
        + parseInt(subscription.seats.field || '0')
        + parseInt(subscription.seats.light || '0');
};


// Count the total active seats within the subscription
export const countTotalActiveSeats = (subscription_lines: SubscriptionLine[]) => {
    // Initialize the seats variable
    let seats = 0;

    // Loop through the subscription lines
    subscription_lines.forEach((line: SubscriptionLine) => {

        // Sum the quantity of the active seat subscription lines
        if (
            (line.type === 'seats' || line.type === 'field_seats' || line.type === 'light_seats') 
            && line.is_active && line.quantity
        ) {
            seats += line.quantity;
        };
    });

    return seats;
};


// Count the total active normal seats within the subscription
export const countActiveNormalSeats = (subscription_lines?: SubscriptionLine[]) => {

    // Return zero if no subscription lines are given
    if (!subscription_lines) return 0;

    // Initialize the seats variable
    let seats = 0;

    // Loop through the subscription lines, sum the active normal seats
    subscription_lines.forEach((line: SubscriptionLine) => {
        if (line.type === 'seats' && line.is_active && line.quantity) {
            seats += line.quantity;
        };
    });

    return seats;
};


// Count the total active field seats within the subscription
export const countActiveFieldSeats = (subscription_lines?: SubscriptionLine[]) => {

    // Return zero if no subscription lines are given
    if (!subscription_lines) return 0;

    // Initialize the seats variable
    let seats = 0;

    // Loop through the subscription lines, sum the field normal seats
    subscription_lines.forEach((line: SubscriptionLine) => {
        if (line.type === 'field_seats' && line.is_active && line.quantity) {
            seats += line.quantity;
        };
    });

    return seats;
};


// Count the total active light seats within the subscription
export const countActiveLightSeats = (subscription_lines?: SubscriptionLine[]) => {

    // Return zero if no subscription lines are given
    if (!subscription_lines) return 0;

    // Initialize the seats variable
    let seats = 0;

    // Loop through the subscription lines, sum the active normal seats
    subscription_lines.forEach((line: SubscriptionLine) => {
        if (line.type === 'light_seats' && line.is_active && line.quantity) {
            seats += line.quantity;
        };
    });

    return seats;
};


// Gets the seat price based on the type of seat of the current subscription received by the backend
export const getCurrentSubscriptionSeatPrice = (
    seatsType: 'normal_seats' | 'field_seats' | 'light_seats', 
    userLocale: string, 
    subscription_lines?: SubscriptionLine[]
) => {

    // Return zero if no subscription lines are given
    if (!subscription_lines) {
        return 0;
    }

    // Convert normal seats into seats
    const convertedSeatsType = seatsType === 'normal_seats' ? 'seats' : seatsType;

    // Find the first active subscription line of the seats type
    const seatsLine = subscription_lines.find(line =>
        line.type === convertedSeatsType && line.is_active
    );

    // Get the announced price from the seats line, the display price is the custom price of the environment
    const total = seatsLine ? parseFloat(seatsLine.display_price ?? seatsLine.price) : 0

    // Check wheter the total has decimals. If the total has one decimal, convert it to two decimals
    const hasDecimals = total % 1 !== 0;
    const decimalPlaces = hasDecimals
        ? (total.toString().split('.')[1]?.length === 1 ? 2 : 2)
        : 0;

    // Format the amount based on the user locale
    const formattedAmount = new Intl.NumberFormat(userLocale, {
        style: "decimal",
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(total);

    return formattedAmount;
};


// Get the seat price based on the type of seat and duration of the new subscription
export const getNewSubscriptionSeatPrice = (
    seatType: string,
    packages: SubscriptionPackage[],
    subscription: SubscriptionData, 
    userLocale: string, 
) => {
    // Get the selected package data
    const selectedPackage = packages.find(pkg => pkg.key === subscription.package);

    // Initialize the seat price
    let seatPrice = 0;

    // Get the right seat price based on the duration
    if (selectedPackage) {
        if (seatType === 'normal_seat') {
            if (subscription.duration === 'yearly') {
                seatPrice = safePriceNumber(selectedPackage.yearly_price)
            }

            if (subscription.duration === 'monthly') {
                seatPrice = safePriceNumber(selectedPackage.monthly_price)
            }
        }

        // Calculate the field seat price based on the duration
        if (seatType === 'field_seat') {
            if (subscription.duration === 'yearly') {
                seatPrice = safePriceNumber(selectedPackage.yearly_field_price)
            }
    
            if (subscription.duration === 'monthly') {
                seatPrice = safePriceNumber(selectedPackage.monthly_field_price)
            }
        }

        // Calculate the light seat price based on the duration
        if (seatType === 'light_seat') {
            if (subscription.duration === 'yearly') {
                seatPrice = safePriceNumber(selectedPackage.yearly_light_price)
            }
    
            if (subscription.duration === 'monthly') {
                seatPrice = safePriceNumber(selectedPackage.monthly_light_price)
            }
        }
    };

    // Check wheter the seat price has decimals. If the seat price has one decimal, convert it to two decimals
    const hasDecimals = seatPrice % 1 !== 0;
    const decimalPlaces = hasDecimals
        ? (seatPrice.toString().split('.')[1]?.length === 1 ? 2 : 2)
        : 0;

    // Format the amount based on the user locale
    const formattedAmount = new Intl.NumberFormat(userLocale, {
        style: "decimal",
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(seatPrice);

    return formattedAmount;
};


// Calculate the current total monthly or yearly subscription amount, received by the backend, with or without vat, and discounts subtracted
export const calculateCurrentSubscriptionAmount = ({ 
    subscription_lines, displayPrice, returnAmount, userLocale, returnAsNumber, useDisplayPrice
}: { 
    subscription_lines: SubscriptionLine[], 
    displayPrice: string,  // Returns the price per month or per year
    returnAmount: 'vatOnly' | 'totalExclVat' | 'totalInclVat', 
    userLocale: string
    returnAsNumber?: boolean,
    useDisplayPrice?: boolean }  // Uses the announced custom price of seat lines instead of the paid price
) => {

    // Get the total active seats of the subscription
    const totalSeats = countTotalActiveSeats(subscription_lines);
    
    // Initialize the total value
    let total = 0;
    let discount = 0;
    let discountPercentage = 0;

    // Loop through the subscription lines
    subscription_lines.forEach((line: SubscriptionLine) => {

        // Only sum active subscription lines
        if (line.is_active && line.quantity && line.price) {

            // Handle seat lines
            if (line.type === 'seats' || line.type === 'field_seats' || line.type === 'light_seats') {

                // Calculate the line total, on the announced price when requested
                const seatPrice = useDisplayPrice ? (line.display_price ?? line.price) : line.price;
                const lineTotal = parseFloat(seatPrice) * line.quantity;

                // Sum the line total to the total
                total += lineTotal;
            };

            // Handle additional module lines
            if (line.type === 'additional_module') {

                // Handle per unit prices
                if (line.price_type === 'per_unit') {

                    // Calculate the line total
                    const lineTotal = parseFloat(line.price) * line.quantity;

                    // Sum the line total to the total
                    total += lineTotal;
                };

                // Handle per seat prices
                if (line.price_type === 'per_seat') {

                    // If a seats threshold is set, and the seats are higher, add the price above threshold
                    if (line.seats_threshold && line.price_above_threshold && (totalSeats > line.seats_threshold)) {

                        // Calculate the line price above threshold
                        const linePriceAboveThreshold = parseFloat(line.price_above_threshold) * line.quantity;

                        // Add the line price above threshold to the total
                        total += linePriceAboveThreshold;

                    // If no seats threshold is set, add the price multiplied by the total seats
                    } else {

                        // Calculate the line total
                        const linePrice = parseFloat(line.price) * line.quantity;

                        // Multiply the line price by the amount of active seats
                        const lineTotal = linePrice * totalSeats;

                        // Sum the line total to the total
                        total += lineTotal;
                    }                    
                };
            }

            // Count the discount percentage
            if (line.type === 'discount') {

                // In case of a discount percentage, count the price as percentage, for example 0.08 for 8%
                if (line.price_type === 'discount_percentage') {
                    discountPercentage += parseFloat(line.price);
                };

                // In case of a per unit price, count the total discount
                if (line.price_type === 'per_unit') {

                    // Calculate the line total
                    const lineDiscount = parseFloat(line.price) * line.quantity;

                    // Sum the line discount to the total discount
                    discount += lineDiscount;
                };
            };
        }
    });

    // When the display price is 'yearly', multiply the total and discount by 12 months
    if (displayPrice === 'yearly') {
        total *= 12;
        discount *= 12;
    };

    // Calculate the total discount amount
    const discountAmount = (total * discountPercentage) + discount;

    // Calculate the discounted total
    total = total - discountAmount;

    // Calculate the return amount
    if (returnAmount === 'vatOnly') {
        // Calculate the vat only
        total = (total * 1.21) - total;

    } else if (returnAmount === 'totalInclVat') {
        // Calculate the total incl vat
        total *= 1.21;
    };

    // Returns the raw amount as number if given
    if (returnAsNumber) {
        return total;
    };

    // Check wheter the total has decimals. If the total has one decimal, convert it to two decimals
    const hasDecimals = total % 1 !== 0;
    const decimalPlaces = hasDecimals
        ? (total.toString().split('.')[1]?.length === 1 ? 2 : 2)
        : 0;

    // Format the amount based on the user locale
    const formattedAmount = new Intl.NumberFormat(userLocale, {
        style: "decimal",
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(total);

    return formattedAmount;
};


// Calculate the new total subscription amount, configured by the frontend wizard, with or without vat, displayed as month or year price
export const calculateNewSubscriptionAmount = ({ 
    packages, 
    additionalModules,
    updatedSubscription, 
    subscriptionDuration, 
    displayPrice, 
    returnAmount, 
    userLocale, 
    returnAsNumber 
}: {
    packages: SubscriptionPackage[],
    additionalModules: AdditionalModule[],
    updatedSubscription: SubscriptionData,
    returnAmount: 'vatOnly' | 'totalExclVat' | 'totalInclVat', 
    userLocale: string 
    displayPrice?: string,  // Returns the price per month or per year
    subscriptionDuration?: string 
    returnAsNumber?: boolean }
) => {

    // Get the selected package
    const selectedPackage = packages.find(pkg => pkg.key === updatedSubscription.package);

    // Handle if the selected package exists
    if (selectedPackage) {

        // Get the amount of normal seats, set as minimum to 1
        const normalSeats = parseInt(updatedSubscription.seats.normal || "1");

        // Get the seat prices, missing prices fall back to zero
        const monthlyPricePerSeat = safePriceNumber(selectedPackage.monthly_price);
        const yearlyPricePerMonthPerSeat = safePriceNumber(selectedPackage.yearly_price);
        const monthlyFieldPricePerSeat = safePriceNumber(selectedPackage.monthly_field_price);
        const yearlyFieldPricePerMonthPerSeat = safePriceNumber(selectedPackage.yearly_field_price);
        const monthlyLightPricePerSeat = safePriceNumber(selectedPackage.monthly_light_price);
        const yearlyLightPricePerMonthPerSeat = safePriceNumber(selectedPackage.yearly_light_price);
        
        // Calculate the monthly price of the normal seats
        let totalMonthlyPrice = monthlyPricePerSeat * normalSeats;
        let totalYearlyPricePerMonth = yearlyPricePerMonthPerSeat * normalSeats;

        // If there are field seats, add it to the monthly price
        if (updatedSubscription.seats.field) {

            // Convert the field seats into a number
            const fieldSeats = parseInt(updatedSubscription.seats.field);

            // Calculate the price with the field users
            totalMonthlyPrice += monthlyFieldPricePerSeat * fieldSeats;
            totalYearlyPricePerMonth += yearlyFieldPricePerMonthPerSeat * fieldSeats;
        };

        // If there are light seats, add it to the monthly price
        if (updatedSubscription.seats.light) {

            // Convert the light seats into a number
            const lightSeats = parseInt(updatedSubscription.seats.light);

            // Calculate the price with the light users
            totalMonthlyPrice += monthlyLightPricePerSeat * lightSeats;
            totalYearlyPricePerMonth += yearlyLightPricePerMonthPerSeat * lightSeats;
        };

        // If there are additional modules, add it to the monthly price
        if (updatedSubscription.additional_modules) {

            // Calculate the total seats
            let totalSeats = normalSeats;
            if (updatedSubscription.seats.field) {
                totalSeats += parseInt(updatedSubscription.seats.field);
            };
            if (updatedSubscription.seats.light) {
                totalSeats += parseInt(updatedSubscription.seats.light);
            };

            // Loop through the additional modules
            updatedSubscription.additional_modules.forEach((module: { name: string }) => {

                // Get the additional module option
                const moduleOption = additionalModules.find(mod => mod.feature === module.name);

                if (moduleOption) {

                    // Add the monthly module price for the total seats to the totals
                    const modulePrice = getModuleMonthlyPriceForSeats(moduleOption, totalSeats);
                    totalMonthlyPrice += modulePrice;
                    totalYearlyPricePerMonth += modulePrice;
                }
            })
        }

        // Initialize amount variable
        let amount = 0;

        // If no subscription duration is given, fallback to the duration of the updated subscription
        if (!subscriptionDuration) {
            subscriptionDuration = updatedSubscription.duration;
        };

        // Handle yearly subscription duration
        if (subscriptionDuration === 'yearly') {

            // Calculate the yearly price
            amount = totalYearlyPricePerMonth * 12;

            // If the display price is set to monthly, return the monthly price of the yearly subscription
            if (displayPrice === 'monthly') {
                amount = totalYearlyPricePerMonth;
            }

            // Calculate the return amount
            if (returnAmount === 'vatOnly') {
                // Calculate the vat only
                amount = (amount * 1.21) - amount;

            } else if (returnAmount === 'totalInclVat') {
                // Calculate the total incl vat
                amount *= 1.21;
            }
        };

        // Handle monthly subscription duration
        if (subscriptionDuration === 'monthly') {

            // Get the total monthly price
            amount = totalMonthlyPrice;

            // If the display price is set to yearly, return the yearly price of the monthly subscription
            if (displayPrice === 'yearly') {
                amount = totalMonthlyPrice * 12;
            }

            // Calculate the return amount
            if (returnAmount === 'vatOnly') {
                // Calculate the vat only
                amount = (amount * 1.21) - amount;

            } else if (returnAmount === 'totalInclVat') {
                // Calculate the total incl vat
                amount *= 1.21;
            }
        };

        // Returns the raw amount as number if given
        if (returnAsNumber) {
            return amount;
        };

        // Check wheter the amount has decimals. If the amount has one decimal, convert it to two decimals
        const hasDecimals = amount % 1 !== 0;
        const decimalPlaces = hasDecimals
            ? (amount.toString().split('.')[1]?.length === 1 ? 2 : 2)
            : 0;

        // Format the amount based on the user locale
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        }).format(amount);

        return formattedAmount;   
    }
};

// Calculate the price of the chosen implementation package in the new subscription
export const calculateImplementationPackagePrice = ({
    implementationPackage, implementationPackages, returnAmount, userLocale, returnAsNumber
}: {
    implementationPackage: number,
    implementationPackages: ImplementationPackage[],
    returnAmount: 'vatOnly' | 'totalExclVat' | 'totalInclVat',
    userLocale: string
    returnAsNumber?: boolean }
) => {

    // Get the chosen implementation package object
    const implementationPackageObject = implementationPackages.find(implementation => implementation.id === implementationPackage);

    // Get the price of the chosen implementation package
    const implementationPrice = implementationPackageObject ? parseFloat(implementationPackageObject.price) : 0;

    if (implementationPrice) {

        // Initialize the amount variable
        let amount = implementationPrice;

        // Calculate the return amount as vat only
        if (returnAmount === 'vatOnly') {
            amount = (amount * 1.21) - amount;

        // Calculate the total incl vat
        } else if (returnAmount === 'totalInclVat') {
            amount *= 1.21;
        }

        // Returns the raw amount as number if given
        if (returnAsNumber) {
            return amount;
        };

        // Check wheter the amount has decimals. If the amount has one decimal, convert it to two decimals
        const hasDecimals = amount % 1 !== 0;
        const decimalPlaces = hasDecimals
            ? (amount.toString().split('.')[1]?.length === 1 ? 2 : 2)
            : 0;

        // Format the amount based on the user locale
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        }).format(amount);

        return formattedAmount;  
    };

    return 0;
};


// Calculate the remaining fraction of the current subscription period
export const calculateCurrentSubscriptionPeriodFraction = (subscriptionEndDate: string, duration: string) => {

    // Convert the subscription end date to a date object
    const endDate = new Date(subscriptionEndDate);

    // Initialize and calculate the start date based on the given duration
    const startDate = new Date(endDate);

    // In case of yearly duration, calculate the start date back to one year ago
    if (duration === 'yearly') {
        startDate.setFullYear(startDate.getFullYear() - 1);
    };

    // In case of monthly duration, calculate the start date back to one month ago
    if (duration === 'monthly') {
        startDate.setMonth(startDate.getMonth() - 1);
    };

    // Set the update date to today
    const upgradeDate = new Date();

    // Subtract one day from the upgrade date, to make it equal to the subscription period
    upgradeDate.setDate(upgradeDate.getDate() - 1);

    // To calculate the difference right, set the time equal to the start- and end dates time
    upgradeDate.setHours(endDate.getHours(), endDate.getMinutes(), endDate.getSeconds(), 0);

    // Convert the dates to milliseconds
    const endDateMilliseconds = endDate.getTime();
    const startDateMilliseconds = startDate.getTime();
    const upgradeDateMilliseconds = upgradeDate.getTime();

    // Calculate the amount of milliseconds in a day
    const dayMilliseconds = 1000 * 60 * 60 * 24;

    // Calculate the total days of the total (original) subscription
    const totalDays = Math.round((endDateMilliseconds - startDateMilliseconds) / dayMilliseconds);

    // Calculate the remaining days of the original subscription period
    const remainingDays = Math.round((endDateMilliseconds - upgradeDateMilliseconds) / dayMilliseconds);

    // Calculate the remaining subscription period fraction
    const remainingFraction = remainingDays / totalDays;

    const roundedFraction = Math.round(remainingFraction * 100) / 100

    return roundedFraction;
};


// Calculates the already paid amount for the remaining period
export const calculateAlreadyPaidAmount = (
    currentSubscription: SubscriptionData, 
    userLocale: string,
    returnAsNumber?: boolean
) => {

    // Return zero if current subscription is a trial
    if (currentSubscription.is_trial) {
        return 0;
    }

    // Initialize the remaining fraction and current subscription amount variables
    let remainingFraction = 1;
    let currentSubscriptionAmount = 0;

    // Calculate the remaining fraction of the current subscription
    if (currentSubscription.end_date) {
        remainingFraction = calculateCurrentSubscriptionPeriodFraction(currentSubscription.end_date, currentSubscription.duration);
    };

    // Calculate the total paid amount of the current subscription
    if (currentSubscription.subscription_lines) {
        currentSubscriptionAmount = Number(calculateCurrentSubscriptionAmount({
            subscription_lines: currentSubscription.subscription_lines,
            displayPrice: currentSubscription.duration,
            returnAmount: 'totalInclVat',
            userLocale,
            returnAsNumber: true
        }));
    }

    // Calculate the already paid amount for the remaining period
    const alreadyPaidAmount = currentSubscriptionAmount * remainingFraction;

    // Return as raw number if given
    if (returnAsNumber) {
        return alreadyPaidAmount;
    };

    // Check wheter the amount has decimals. If the amount has one decimal, convert it to two decimals
    const hasDecimals = alreadyPaidAmount % 1 !== 0;
    const decimalPlaces = hasDecimals
        ? (alreadyPaidAmount.toString().split('.')[1]?.length === 1 ? 2 : 2)
        : 0;

    // Format the amount based on the user locale
    const formattedAmount = new Intl.NumberFormat(userLocale, {
        style: "decimal",
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(alreadyPaidAmount);

    return formattedAmount;
};


// Get the next invoice date
export const getNextInvoiceDate = (subscription: SubscriptionData, userLocale: string) => {

    // Convert the end date of the subscription to a date
    const nextInvoiceDate = new Date(subscription.end_date!);

    // Add one day to the date
    nextInvoiceDate.setDate(nextInvoiceDate.getDate() + 1);

    // Format the date based on the user locale
    const formattedDate = nextInvoiceDate.toLocaleDateString(userLocale, { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
    });

    return formattedDate;
};